/**
 * Optional read-back: after each saved ball, the system speaks the
 * outcome via the browser's SpeechSynthesis API so the operator gets
 * a hands-free verification of what was recorded.
 *
 * Founder ask 2026-05-26: T125 — confirmation without looking at
 * the screen. Especially useful when paired with voice scoring +
 * hands-free mode where the operator isn't watching the ball state.
 *
 * Opt-in toggle persisted to localStorage. Operator can disable
 * during noisy moments. We deliberately keep the spoken text TIGHT
 * — "four boundary, cover drive" rather than reading every field.
 */
import { useCallback, useEffect, useRef, useState } from "react";


const STORAGE_KEY = "zcd_voice_readback_enabled";

function defaultEnabled() {
  try { return localStorage.getItem(STORAGE_KEY) === "1"; }
  catch { return false; }
}


export function useBallReadback() {
  const [enabled, setEnabled] = useState(defaultEnabled);
  const supported = typeof window !== "undefined"
    && typeof window.speechSynthesis !== "undefined"
    && typeof window.SpeechSynthesisUtterance !== "undefined";

  const lastSpokenRef = useRef("");

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch {}
      return next;
    });
  }, []);

  // Cancel any in-flight speech when the component unmounts.
  useEffect(() => {
    return () => {
      try { window.speechSynthesis?.cancel(); } catch {}
    };
  }, []);

  // Read out a ball-outcome summary. Caller assembles the short
  // string ("four boundary, cover drive") — keeps speech rules out
  // of the hook so callers can choose what's worth speaking.
  // 2026-05-27 founder ask: TTS read-back is desktop-only — operators
  // hit the BBP screen on phone/tablet and got surprised by spoken
  // outcomes. Even if a returning user has the localStorage flag set
  // to enabled from a previous desktop session, the speak() is a
  // no-op on viewports below lg (1024px).
  const speak = useCallback((text) => {
    if (!enabled || !supported || !text) return;
    if (typeof window !== "undefined" && window.matchMedia) {
      try {
        if (!window.matchMedia("(min-width: 1024px)").matches) return;
      } catch { return; }
    }
    if (text === lastSpokenRef.current) return; // de-dup back-to-back
    lastSpokenRef.current = text;
    try {
      window.speechSynthesis.cancel();
      const u = new window.SpeechSynthesisUtterance(text);
      u.lang = "en-IN";
      u.rate = 1.1;       // slightly faster than default — scorers move fast
      u.pitch = 1.0;
      u.volume = 0.85;
      window.speechSynthesis.speak(u);
    } catch (exc) {
      // Quiet failure — read-back is a comfort feature, never block.
      // eslint-disable-next-line no-console
      console.warn("readback speak failed:", exc);
    }
  }, [enabled, supported]);

  return { enabled, supported, toggle, speak };
}


// Compose a short, speakable summary of a ball's outcome. Caller
// hands in the post-save payload; we return text or null (silence
// when nothing meaningful happened).
export function ballOutcomeSummary(payload) {
  if (!payload) return null;
  const parts = [];
  if (payload.is_wide)     parts.push("wide");
  if (payload.is_no_ball)  parts.push("no ball");
  if (payload.is_bye)      parts.push("bye");
  if (payload.is_leg_bye)  parts.push("leg bye");
  if (payload.is_dead_ball) parts.push("dead ball");
  if (payload.is_free_hit) parts.push("free hit");
  const runs = payload.completed_runs ?? 0;
  if (runs > 0 || (!payload.is_wide && !payload.is_no_ball && !payload.has_wicket && parts.length === 0)) {
    if (runs === 0) parts.push("dot");
    else if (runs === 1) parts.push("one run");
    else if (runs === 4) parts.push("four boundary");
    else if (runs === 6) parts.push("six");
    else parts.push(`${runs} runs`);
  }
  if (payload.has_wicket) {
    parts.push(`wicket ${(payload.wicket_type || "").toLowerCase().replace(/_/g, " ")}`);
  }
  const stroke = payload.analytics?.stroke;
  if (stroke) parts.push(stroke.toLowerCase());
  return parts.length ? parts.join(", ") : null;
}
